// frontend/src/components/ProfileFlowers.js

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  CaretCircleLeft,
  CaretCircleRight,
  Check,
  X,
  Smiley,
  MegaphoneSimple,
} from "phosphor-react";

import { api } from "../api";
import {
  SHOUTOUT_FONTS,
  getShoutoutFont,
} from "../constants/shoutoutFonts";
import ShoutoutWallModal from "./ShoutoutWallModal";
import { DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";

const FLOWER_PROMPTS = [
  {
    key: "proud",
    prefix: "I'm proud of you for",
    placeholder: "not letting the 5 a.m. alarm win",
  },
  {
    key: "taught",
    prefix: "You've taught me",
    placeholder: "to diva down",
  },
  {
    key: "admire",
    prefix: "I admire",
    placeholder: "how you always show up for your friends",
  },
  {
    key: "rockstar",
    prefix: "You are",
    placeholder: "Barry's #1 draft pick",
  },
  {
    key: "dont_forget",
    prefix: "Don't forget",
    placeholder: "how far you've already come",
  },
  {
    key: "love_that",
    prefix: "I love that you",
    placeholder: "never give up",
  },
  {
    key: "thank_you",
    prefix: "Thank you for",
    placeholder: "believing in me",
  },
  {
    key: "remember",
    prefix: "I'll always remember",
    placeholder: "conquering the impossible of playing tennis in NYC together",
  },
  {
    key: "inspire",
    prefix: "You inspire me to",
    placeholder: "be a little braver",
  },
  {
    key: "hope_you_know",
    prefix: "I hope you know",
    placeholder: "how much people root for you",
  },
  {
    key: "never_stop",
    prefix: "Never stop",
    placeholder: "doing things for the plot",
  },
  {
    key: "trust",
    prefix: "I'll trust you with",
    placeholder: "picking the wine, always",
  },
  {
    key: "elite_at",
    prefix: "You're weirdly good at",
    placeholder: "scoring sourdough like it's an art form",
  },
  {
    key: "very_you",
    prefix: "The most you thing ever is",
    placeholder: "reading a book on the treadmill",
  },
  {
    key: "most_likely_to",
    prefix: "Most likely to",
    placeholder: "hit 100 Solidcore classes before the rest of us hit 25",
  },
  {
    key: "better_because",
    prefix: "Life is better because you",
    placeholder: "are my favorite side quest partner",
  },
  {
    key: "virtual_high_five",
    prefix: "Your virtual high five",
    placeholder: "💙 ✨",
  },
];

const getDailyPromptIndex = (userId) => {
  const now = new Date();

  const dateKey = `${now.getFullYear()}-${
    now.getMonth() + 1
  }-${now.getDate()}-${userId || 0}`;

  let hash = 0;

  for (let i = 0; i < dateKey.length; i += 1) {
    hash =
      (hash * 31 + dateKey.charCodeAt(i)) >>> 0;
  }

  return hash % FLOWER_PROMPTS.length;
};

export default function ProfileFlowers({
  profileUser,
  currentUser,
  isSelf = false,
  readOnly = false,

  // optional page/mobile hooks
  onOpenWall,
  hideWallLauncher = false,
  openGiveSignal = 0,
  openWallAfterSend = true,
  onShoutoutSent,
}) {
  const [giveOpen, setGiveOpen] = useState(false);
  const [wallOpen, setWallOpen] = useState(false);

  const [promptIndex, setPromptIndex] = useState(() =>
    getDailyPromptIndex(profileUser?.id)
  );

  const [customMode, setCustomMode] = useState(false);
  const [body, setBody] = useState("");
  const [isPublic, setIsPublic] = useState(true);

  const [fontId, setFontId] = useState(SHOUTOUT_FONTS[0].id);

  const fontDef = getShoutoutFont(fontId);

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  const [confirmingSend, setConfirmingSend] = useState(false);

  const [wallRefreshKey, setWallRefreshKey] =
    useState(0);
  
  const [viewerUser, setViewerUser] = useState(currentUser || null);

  const displayName =
    profileUser?.display_name ||
    profileUser?.name ||
    profileUser?.username ||
    "this person";

  const profilePhoto =
    profileUser?.profile_pic_url ||
    DEFAULT_PROFILE_IMAGE;

  const prompt =
    FLOWER_PROMPTS[
      promptIndex % FLOWER_PROMPTS.length
    ];

  const previewPrefix =
    customMode || prompt.key === "virtual_high_five"
      ? null
      : prompt.prefix;

  const previewBody =
    prompt.key === "virtual_high_five"
      ? `🫸 ${body.trim()}`
      : body.trim();

  const previewSenderName =
    viewerUser?.username ||
    viewerUser?.name ||
    "";

  const canGiveFlowers =
    !readOnly &&
    !isSelf &&
    profileUser?.flowers_enabled !== false;
  
  const canViewFlowers =
    !readOnly &&
    profileUser?.flowers_enabled !== false;

  useEffect(() => {
    setPromptIndex(
      getDailyPromptIndex(profileUser?.id)
    );
  }, [profileUser?.id]);

  useEffect(() => {
    if (currentUser?.username) {
      setViewerUser(currentUser);
      return;
    }

    let cancelled = false;

    const loadViewer = async () => {
      try {
        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token");

        if (!token) return;

        const { data } = await api.get("/users/me", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!cancelled) {
          setViewerUser(data);
        }
      } catch {
        // Leave preview handle blank if viewer cannot be loaded.
      }
    };

    loadViewer();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  useEffect(() => {
    if (!openGiveSignal) return;
    if (!canGiveFlowers) return;

    resetComposer();
    setGiveOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openGiveSignal]);

  const previousPrompt = () => {
    setCustomMode(false);
    setBody("");

    setPromptIndex((index) =>
      index <= 0
        ? FLOWER_PROMPTS.length - 1
        : index - 1
    );
  };

  const nextPrompt = () => {
    setCustomMode(false);
    setBody("");

    setPromptIndex((index) =>
      index >= FLOWER_PROMPTS.length - 1
        ? 0
        : index + 1
    );
  };

  const resetComposer = () => {
    setBody("");
    setCustomMode(false);
    setFontId(SHOUTOUT_FONTS[0].id);
    setIsPublic(true);
    setSendError("");

    setPromptIndex(
      getDailyPromptIndex(profileUser?.id)
    );
  };

  const closeGive = () => {
    setGiveOpen(false);
    resetComposer();
  };

  const getShoutoutPreview = () => {
    const cleanBody = body.trim();

    if (!cleanBody) return "";

    if (customMode) {
      return cleanBody;
    }

    if (prompt.key === "virtual_high_five") {
      return `🫸 ${cleanBody}`;
    }

    return `${prompt.prefix} ${cleanBody}`.trim();
  };

  const submitFlower = async () => {
    const cleanBody = body.trim();

    if (!cleanBody || !profileUser?.id) {
      setSendError(
        "Write something before sending your shoutout."
      );
      return;
    }

    setSending(true);
    setSendError("");

    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      await api.post(
        `/users/${profileUser.id}/flowers`,
        {
          prompt_key: customMode
            ? "custom"
            : prompt.key,

          prefix:
            customMode ||
            prompt.key === "virtual_high_five"
              ? null
              : prompt.prefix,

          body:
            prompt.key === "virtual_high_five"
              ? `🫸 ${cleanBody}`
              : cleanBody,

          is_public: isPublic,
          font_id: fontId,
        },
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
        }
      );

      setConfirmingSend(false);
      setGiveOpen(false);

      setWallRefreshKey((key) => key + 1);

      if (typeof onShoutoutSent === "function") {
        onShoutoutSent();
      }

      if (openWallAfterSend) {
        setWallOpen(true);
      }

      resetComposer();
    } catch (error) {
      setConfirmingSend(false);

      setSendError(
        error?.response?.data?.detail ||
          "Could not send your shoutout."
      );
    } finally {
      setSending(false);
    }
  };

  const openWall = () => {
    if (typeof onOpenWall === "function") {
      onOpenWall();
      return;
    }

    setWallOpen(true);
  };

  return (
    <>
      {canViewFlowers && !hideWallLauncher && (
        <div className="mt-4 flex w-full flex-col items-center justify-center gap-1.5">
          <button
            type="button"
            onClick={openWall}
            className="
              inline-flex items-center gap-1
              !min-h-0 !min-w-0
              rounded-full bg-blue-800
              px-3 py-1
              text-xs font-semibold text-white
              ring-2 ring-blue-300
              hover:animate-[wiggle_1s_ease-in-out_infinite]
            "
          >
            <MegaphoneSimple size={13} weight="duotone" />
            Shoutouts
          </button>
        </div>
      )}

      {/* GIVE FLOWERS MODAL */}
      {giveOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <button
              type="button"
              aria-label="Close"
              onClick={closeGive}
              className="absolute inset-0 bg-black/40"
            />

            <div
              className="
                relative z-10
                w-full max-w-md
                max-h-[90vh] overflow-y-auto
                rounded-[30px] bg-white
                px-5 py-5
                shadow-xl
              "
            >
              <button
                type="button"
                onClick={closeGive}
                className="
                  absolute right-4 top-4
                  inline-flex h-7 w-7
                  items-center justify-center
                  rounded-full
                  !min-h-0 !min-w-0
                "
              >
                <X size={14} />
              </button>

              <div className="text-center">
                <MegaphoneSimple
                  size={28}
                  weight="duotone"
                  className="mx-auto text-blue-600"
                />

                <h2 className="mt-0.5 text-lg font-black tracking-[-0.03em] text-gray-900">
                  Give a shoutout
                </h2>
              </div>

              <div className="mt-2">
                {!customMode ? (
                  <>
                    <div className="flex items-center justify-between gap-3 px-2">
                      <button
                        type="button"
                        onClick={previousPrompt}
                        className="
                          inline-flex shrink-0 items-center justify-center
                          text-blue-600
                          hover:text-blue-800
                        "
                        aria-label="Previous prompt"
                      >
                        <CaretCircleLeft size={20} weight="duotone" />
                      </button>

                      <p className="text-center text-base font-bold text-gray-900">
                        {prompt.prefix}
                      </p>

                      <button
                        type="button"
                        onClick={nextPrompt}
                        className="
                          inline-flex shrink-0 items-center justify-center
                          text-blue-600
                          hover:text-blue-800
                        "
                        aria-label="Next prompt"
                      >
                        <CaretCircleRight size={20} weight="duotone" />
                      </button>
                    </div>

                    {prompt.key === "virtual_high_five" ? (
                      <div
                        className="
                          mt-3 flex w-full items-start
                          rounded-2xl border border-gray-200
                          px-3 py-3
                          focus-within:border-2
                          focus-within:border-blue-500
                        "
                      >
                        <span className="mr-1.5 text-sm">
                          🫸
                        </span>

                        <textarea
                          value={body}
                          onChange={(event) =>
                            setBody(event.target.value)
                          }
                          maxLength={20}
                          rows={1}
                          autoFocus
                          placeholder="💙 ✨"
                          style={{
                            fontFamily: fontDef.css,
                            fontWeight: fontDef.weight,
                          }}
                          className="
                            min-h-0 flex-1 resize-none
                            border-0 bg-transparent
                            p-0 text-sm outline-none
                            focus:ring-0
                          "
                        />
                      </div>
                    ) : (
                      <textarea
                        value={body}
                        onChange={(event) =>
                          setBody(event.target.value)
                        }
                        maxLength={1000}
                        rows={4}
                        autoFocus
                        placeholder={prompt.placeholder}
                        style={{
                          fontFamily: fontDef.css,
                          fontWeight: fontDef.weight,
                        }}
                        className="
                          mt-3 w-full resize-none
                          rounded-2xl border border-gray-200
                          px-3 py-3 text-sm
                          outline-none
                          focus:border-2
                          focus:border-blue-500
                        "
                      />
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-center text-base font-bold text-gray-900">
                      Say whatever you want
                    </p>

                    <textarea
                      value={body}
                      onChange={(event) =>
                        setBody(event.target.value)
                      }
                      maxLength={1000}
                      rows={4}
                      autoFocus
                      style={{
                        fontFamily: fontDef.css,
                        fontWeight: fontDef.weight,
                      }}
                      className="
                        mt-3 w-full resize-none
                        rounded-2xl border border-gray-200
                        px-3 py-3 text-sm
                        outline-none
                        focus:border-2
                        focus:border-blue-500
                      "
                    />
                  </>
                )}

                <div className="mt-2 flex items-center justify-center gap-2">
                  {SHOUTOUT_FONTS.map((font) => {
                    const active = font.id === fontId;

                    return (
                      <button
                        key={font.id}
                        type="button"
                        onClick={() => setFontId(font.id)}
                        className={`
                          flex h-9 w-9
                          !min-h-0 !min-w-0
                          items-center justify-center
                          rounded-full
                          border
                          text-sm
                          transition
                          ${
                            active
                              ? "border-blue-500 bg-blue-100 text-blue-800"
                              : "border-gray-200 bg-white text-gray-600 hover:bg-blue-50"
                          }
                        `}
                        style={{
                          fontFamily: font.css,
                          fontWeight: font.weight,
                        }}
                      >
                        Aa
                      </button>
                    );
                  })}
                </div>

                <div className="mt-1 flex items-center justify-between px-1">
                  <button
                    type="button"
                    onClick={() => {
                      setCustomMode((value) => !value);
                      setBody("");
                    }}
                    className="
                      !min-h-0 !min-w-0
                      text-xs font-semibold
                      text-blue-600
                      hover:underline
                    "
                  >
                    {customMode
                      ? "Use a prompt"
                      : "Write my own"}
                  </button>

                  <span className="text-[11px] text-gray-400">
                    {body.length}/1000
                  </span>
                </div>
              </div>

              <label
                className="
                  mt-4 flex cursor-pointer
                  items-start gap-2
                  rounded-2xl bg-gray-50
                  px-3 py-2
                "
              >
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(event) =>
                    setIsPublic(event.target.checked)
                  }
                  className="peer sr-only"
                />

                <div
                  className={`
                    mt-0.5 flex h-4 w-4 shrink-0
                    items-center justify-center
                    rounded-full border
                    transition-colors
                    ${
                      isPublic
                        ? "border-blue-500 bg-blue-500"
                        : "border-slate-500 bg-white"
                    }
                  `}
                >
                  <Check
                    size={12}
                    weight="bold"
                    className={`
                      text-white transition-opacity
                      ${
                        isPublic
                          ? "opacity-100"
                          : "opacity-0"
                      }
                    `}
                  />
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-700">
                    Add this to their shoutouts wall
                  </p>

                  <p className="text-[11px] leading-tight text-gray-500">
                    Uncheck to make it private
                  </p>
                </div>
              </label>

              {sendError && (
                <p className="mt-3 text-center text-xs text-red-600">
                  {sendError}
                </p>
              )}

              <button
                type="button"
                disabled={
                  sending || !body.trim()
                }
                onClick={() => {
                  setSendError("");
                  setConfirmingSend(true);
                }}
                className="
                  mt-4 w-full rounded-full
                  bg-blue-600 px-4 py-2
                  text-sm font-semibold text-white
                  !min-h-0 !min-w-0
                  hover:bg-blue-700
                  disabled:opacity-50
                "
              >
                {sending
                  ? "Sending…"
                  : "Send"}
              </button>
            </div>
          </div>,
          document.body
        )}
      
      {/* SHOUTOUT CONFIRMATION */}
      {confirmingSend &&
        createPortal(
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <button
              type="button"
              className="absolute inset-0 bg-black/40"
              onClick={() => {
                if (!sending) {
                  setConfirmingSend(false);
                }
              }}
              aria-label="Back to shoutout"
            />

            <div
              className="
                relative w-full max-w-sm md:max-w-md
                rounded-[32px] bg-white
                px-6 py-6
                shadow-[0_8px_0px_rgba(37,99,235,0.96)]
              "
            >
              <div className="text-center">
                <p className="text-base font-semibold leading-tight text-black">
                  Send this shoutout?
                </p>

                <div
                  className="
                    mx-auto mt-4
                    flex min-h-[150px]
                    items-center justify-center
                    rounded-3xl bg-blue-50
                    !min-h-0 !min-w-0 px-4 py-5
                  "
                >
                  <div
                    className="
                      flex flex-col
                      items-center justify-center
                      text-center
                    "
                  >
                    {previewPrefix && (
                      <p
                        className="
                          whitespace-nowrap
                          text-[13px] font-bold
                          uppercase
                          leading-none
                          tracking-[0.03em]
                          text-blue-500
                        "
                      >
                        {previewPrefix}
                      </p>
                    )}

                    <p
                      className="
                        mt-1
                        whitespace-pre-wrap
                        break-words
                        text-[17px]
                        leading-[1.1]
                        text-[#25366f]
                      "
                      style={{
                        fontFamily: fontDef.css,
                        fontWeight: fontDef.weight,
                      }}
                    >
                      {previewBody}
                    </p>

                    {previewSenderName && (
                      <p className="mt-1 text-[10px] font-medium leading-none text-gray-400">
                        @{previewSenderName}
                      </p>
                    )}
                  </div>
                </div>

                <p className="mt-3 text-xs text-gray-400">
                  {isPublic
                    ? `This will appear on ${displayName}'s shoutouts`
                    : `Only ${displayName} will be able to see this`}
                </p>
              </div>

              <div className="mt-4 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setConfirmingSend(false)
                  }
                  disabled={sending}
                  className="
                    rounded-full border border-blue-700
                    px-3 py-1.5
                    text-sm text-blue-800
                    hover:ring-2 hover:ring-blue-700
                    disabled:opacity-50
                  "
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={submitFlower}
                  disabled={sending}
                  className="
                    rounded-full bg-blue-600
                    px-4 py-2
                    text-sm font-semibold text-white
                    hover:ring-2 hover:ring-blue-300
                    disabled:opacity-50
                  "
                >
                  {sending ? "Sending…" : "Confirm"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <ShoutoutWallModal
        open={wallOpen}
        onClose={() => setWallOpen(false)}
        profileUser={profileUser}
        canGiveShoutout={canGiveFlowers}
        refreshKey={wallRefreshKey}
        onGiveShoutout={() => {
          setWallOpen(false);
          resetComposer();
          setGiveOpen(true);
        }}
      />
    </>
  );
}